import {
  getProductTypeBySlug,
  MAIN_PRODUCT_TYPE_SLUGS,
  type MainProductTypeSlug,
} from "@/lib/generated/product-types";
import { getTool, toolPublicHref } from "@/lib/tools/catalog";
import type { NavLinkGroup, NavLinkItem, NavMenu } from "./types";

type Trade = "buy" | "sell" | "recycle";

const TRADE_VERBS: Record<Trade, { label: string; hubLabel: string }> = {
  buy: { label: "Buy", hubLabel: "All buying options" },
  sell: { label: "Sell", hubLabel: "All selling options" },
  recycle: { label: "Recycle", hubLabel: "All recycling options" },
};

const PRODUCT_FAMILIES = ["Pallets and crates", "Drums, totes and bags", "Boxes and fiber", "Wood and spools", "Supplies and equipment"] as const;

type ProductFamily = (typeof PRODUCT_FAMILIES)[number];

const FAMILY_OF: Record<MainProductTypeSlug, ProductFamily> = {
  pallets: "Pallets and crates",
  "plastic-pallets": "Pallets and crates",
  "pallet-riser": "Pallets and crates",
  "wood-crates": "Pallets and crates",
  "plastic-crates": "Pallets and crates",
  "wire-baskets": "Pallets and crates",
  "metal-cages": "Pallets and crates",
  "ibc-totes": "Drums, totes and bags",
  "metal-drums": "Drums, totes and bags",
  "plastic-drums": "Drums, totes and bags",
  "fiber-drums": "Drums, totes and bags",
  "bulk-bags": "Drums, totes and bags",
  "gaylord-boxes": "Boxes and fiber",
  "shipping-boxes": "Boxes and fiber",
  "moving-boxes": "Boxes and fiber",
  "cardboard-bales": "Boxes and fiber",
  lumber: "Wood and spools",
  "wooden-spools": "Wood and spools",
  "stretch-wrap": "Supplies and equipment",
  strapping: "Supplies and equipment",
  equipment: "Supplies and equipment",
};

function sentenceCaseNoun(name: string): string {
  return name
    .split(" ")
    .map((word) => (word === word.toUpperCase() ? word : word.toLowerCase()))
    .join(" ");
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function productLink(trade: Trade, verb: string, slug: MainProductTypeSlug): NavLinkItem {
  const noun = sentenceCaseNoun(getProductTypeBySlug(slug)?.pluralDisplayName ?? slug);
  return { label: capitalized(noun), ariaLabel: `${verb} ${noun}`, href: `/${trade}-${slug}` };
}

function familyGroups(trade: Trade, verb: string): NavLinkGroup[] {
  return PRODUCT_FAMILIES.map((heading) => ({
    heading,
    links: MAIN_PRODUCT_TYPE_SLUGS.filter((slug) => FAMILY_OF[slug] === heading).map((slug) => productLink(trade, verb, slug)),
  }));
}

function tradeMenu(trade: Trade): NavMenu {
  const { label, hubLabel } = TRADE_VERBS[trade];
  const groups = familyGroups(trade, label);
  return {
    label,
    hub: { label: hubLabel, href: `/${trade}` },
    links: groups.flatMap((group) => group.links),
    groups,
  };
}

const RESOURCES_GROUPS: NavLinkGroup[] = [
  {
    heading: "Tools",
    links: [
      { label: "Truck loading calculator", href: toolPublicHref(getTool("truck-loading")) },
      { label: "Freight calculator", href: toolPublicHref(getTool("freight-calculator")) },
      { label: "Price Index", href: "/price-index" },
      { label: "Grading tool", href: "/grade" },
      { label: "All calculators", href: "/resources/calculators" },
    ],
  },
  {
    heading: "Learn",
    links: [
      { label: "Resource center", href: "/resources" },
      { label: "Blog", href: "/blog" },
      { label: "About us", href: "/about-us" },
      { label: "Support", href: "/contact" },
    ],
  },
];

export const TRADE_MENUS: NavMenu[] = [
  tradeMenu("buy"),
  tradeMenu("sell"),
  tradeMenu("recycle"),
];

export const RESOURCES_MENU: NavMenu = {
  label: "Resources",
  links: RESOURCES_GROUPS.flatMap((group) => group.links),
  groups: RESOURCES_GROUPS,
};

export const NAV_MENUS: NavMenu[] = [...TRADE_MENUS, RESOURCES_MENU];
